import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { CheckoututilService } from './checkoututil.service';
import { AuthorizationService } from '../authorization/authorization.service';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { UrlSignature } from '../common/apiEndPoint';

interface CredentialsResponse {
  success: boolean;
  message: string;
  data?: {
    phoneNumber: string;
    pin: string;
    checkoutId: string;
    referenceID: string;
    email: string;
    paymentStatus: string;
  };
  error?: string;
  currentStatus?: string;
}

@Component({
  selector: 'app-paymentsuccess',
  templateUrl: './paymentsuccess.component.html',
  styleUrls: ['./paymentsuccess.component.scss'],
})
export class PaymentsuccessComponent implements OnInit {
  showContent = false;
  showButton = false;

  checkoutId: string | null = null;
  clientReference: string | null = null;
  isLoading: boolean = true;

  credentials: any = null;
  showCredentials = false;
  credentialsLoading = false;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private checkoutUtils: CheckoututilService,
    private authService: AuthorizationService,
    private toastr: ToastrService,
    private http: HttpClient,
    private urlSignature: UrlSignature
  ) {}




  ngOnInit(): void {
  // ✅ NEW: Check if credentials were passed from payment-pending component
  // This happens when verify-status confirmed Paid and navigated here directly
  const routerState = history.state;
  if (routerState && routerState.referenceID && routerState.pin) {
    console.log('✅ Credentials received from payment-pending via router state');
    this.credentials = {
      referenceID: routerState.referenceID,
      pin: routerState.pin
    };
    this.showCredentials = true;
    this.showContent = true;
    this.showButton = false;
    this.isLoading = false;
    this.toastr.success('Payment confirmed!', 'Success');
    return; // ← skip all the checkoutId / API logic below
  }

  // ── existing logic below unchanged ────────────────────────────────────────
  const recovered = this.authService.recoverAuthenticationData();
  if (recovered) {
    console.log('Authentication recovered for payment verification');
  }

  if (!this.authService.isAuthenticated()) {
    console.error('No valid authentication found after recovery attempt');
    this.toastr.error(
      'Authentication required for payment verification. Please login again.',
      'Authentication Error'
    );
    this.router.navigate(['/login']);
    this.isLoading = false;
    return;
  }

  this.route.queryParamMap.subscribe((params) => {
    const paramCheckoutId = params.get('checkoutid');
    const localCheckoutId = this.checkoutUtils.getCheckoutId();

    if (paramCheckoutId) {
      this.checkoutId = paramCheckoutId;
    } else if (localCheckoutId) {
      this.checkoutId = localCheckoutId;
    } else {
      const sessionCheckoutId = sessionStorage.getItem('checkoutId');
      if (sessionCheckoutId) {
        this.checkoutId = sessionCheckoutId;
      } else {
        this.handleMissingCheckoutId();
        this.isLoading = false;
        return;
      }
    }

    this.clientReference = localStorage.getItem('clientReference');

    if (this.checkoutUtils.isValidCheckoutId(this.checkoutId)) {
      this.processPaymentSuccess();
      this.onRetrieveReferencePinCode();
    } else {
      this.handleInvalidCheckoutId();
    }

    this.cleanupStoredData();
    this.isLoading = false;
  });
}

  private processPaymentSuccess(): void {
    console.log('Processing payment success for checkout ID:', this.checkoutId);
    this.toastr.success('Payment processed successfully!', 'Success');

    this.showContent = true; // Show main success content
    // this.showButton = true;
    console.log('Payment success processing completed');
  }

  private handleMissingCheckoutId(): void {
    console.warn('No checkout ID found, handling missing checkout scenario');

    this.toastr.error('Payment verification failed. Please contact support if you completed a payment.', 'Error');
    setTimeout(() => {
      this.router.navigate(['/checkout']);
    }, 3000);
  }

  private handleInvalidCheckoutId(): void {
    console.warn('Invalid checkout ID format, showing generic success message');

    this.showContent = true;
    this.showButton = false;

    this.toastr.warning('Payment appears successful, but transaction details are incomplete.', 'Warning');
  }

  private cleanupStoredData(): void {
    this.checkoutUtils.clearStoredCheckoutId();
    try {
      localStorage.removeItem('clientReference');
      console.log('Cleaned up stored checkout data');

      this.authService.clearBackupData();
      console.log('Cleared backup authentication data after payment verification');
    } catch (error) {
      console.error('Error cleaning up stored data:', error);
    }
  }

  onRetrieveReferencePinCode(): void {
    if (this.checkoutId) {
      console.log('Retrieving serial code for checkout ID:', this.checkoutId);

      // Check authentication before making API call
      if (!this.authService.isAuthenticated()) {
        this.toastr.error('Authentication expired. Please login again.', 'Authentication Error');
        this.router.navigate(['/login']);
        return;
      }

      this.credentialsLoading = true;
      this.retrieveCredentialsFromAPI();
    } else {
      console.error('Cannot retrieve serial code: No checkout ID available');
      this.toastr.error('Unable to retrieve credentials. Missing checkout information.', 'Error');
    }
  }

  private retrieveCredentialsFromAPI(): void {
    const token = this.authService.getCurrentToken();

    if (!token) {
      this.credentialsLoading = false;
      this.toastr.error('Authentication token not found. Please login again.', 'Authentication Error');
      this.router.navigate(['/login']);
      return;
    }

    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    });

    console.log('Calling API to retrieve credentials...');
    console.log('Full API URL:', this.urlSignature.paymentsuccessurl);
    console.log('Token present:', !!token);
    console.log('Token preview:', token ? token.substring(0, 20) + '...' : 'No token');

    this.http.post<CredentialsResponse>(this.urlSignature.paymentsuccessurl, {}, { headers }).subscribe({
      next: (response) => {
        this.credentialsLoading = false;
        console.log('API Response:', response);

        if (response.success && response.data) {
          this.credentials = response.data;
          this.showCredentials = true;
          this.showButton = false;
          this.toastr.success('Credentials retrieved successfully!', 'Success');
          console.log('Credentials retrieved:', response.data);
        } else {
          this.handleCredentialsError(response);
          this.showButton = true;
        }
      },
      error: (error) => {
        this.credentialsLoading = false;
        this.showButton = true;
        console.error('API Error:', error);

        if (error.status === 400) {
          this.toastr.error('Invalid request. Please check the checkout ID.', 'Bad Request');
        } else if (error.status === 401 || error.status === 403) {
          this.toastr.error('Authentication failed. Please log in again.', 'Unauthorized');
          // try to recover authentication one more time
          const recovered = this.authService.recoverAuthenticationData();
          if (!recovered) {
            this.router.navigate(['/login']);
          } else {
            this.toastr.info('Authentication recovered. Please try again.', 'Retry');
          }
        } else if (error.status === 404) {
          this.toastr.error('Payment transaction not found. Please contact support.', 'Not Found');
        } else if (error.status >= 500) {
          this.toastr.error('Server error. Please try again later.', 'Server Error');
        } else {
          this.toastr.error('Failed to retrieve credentials. Please try again.', 'Error');
        }
      },
    });
  }

  /**
   * @param response
   */
  private handleCredentialsError(response: CredentialsResponse): void {
    console.warn('Credentials retrieval failed:', response);

    switch (response.error) {
      case 'MISSING_CHECKOUT_ID':
        this.toastr.error('Checkout ID is required.', 'Missing Information');
        break;
      case 'PAYMENT_TRANSACTION_NOT_FOUND':
        this.toastr.error('Payment transaction not found. Please contact support.', 'Transaction Not Found');
        break;
      case 'SIGNUP_RECORD_NOT_FOUND':
        this.toastr.error('No registration record found. Please contact support.', 'Record Not Found');
        break;
      case 'PAYMENT_NOT_COMPLETED':
        this.toastr.warning(
          `Payment status: ${response.currentStatus}. Please wait for payment confirmation.`,
          'Payment Pending'
        );
        break;
      case 'CREDENTIALS_NOT_GENERATED':
        this.toastr.error('Credentials not ready yet. Please contact support.', 'Credentials Unavailable');
        break;
      case 'INTERNAL_SERVER_ERROR':
        this.toastr.error('Server error occurred. Please try again later.', 'Server Error');
        break;
      default:
        this.toastr.error(response.message || 'Failed to retrieve credentials.', 'Error');
    }
  }

  /**
   * @param text The text to copy.
   * @param type
   */
  copyToClipboard(text: string, type: string): void {
    if (navigator.clipboard) {
      navigator.clipboard
        .writeText(text)
        .then(() => {
          this.toastr.success(`${type} copied to clipboard!`, 'Copied');
        })
        .catch(() => {
          this.fallbackCopyToClipboard(text, type);
        });
    } else {
      this.fallbackCopyToClipboard(text, type);
    }
  }

  /**
   * @param text
   * @param type .
   */
  private fallbackCopyToClipboard(text: string, type: string): void {
    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.style.position = 'fixed';
    textArea.style.top = '0';
    textArea.style.left = '0';
    textArea.style.width = '2em';
    textArea.style.height = '2em';
    textArea.style.padding = '0';
    textArea.style.border = 'none';
    textArea.style.outline = 'none';
    textArea.style.boxShadow = 'none';
    textArea.style.background = 'transparent';
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();

    try {
      document.execCommand('copy');
      this.toastr.success(`${type} copied to clipboard!`, 'Copied');
    } catch (err) {
      this.toastr.error(`Failed to copy ${type}. Please copy manually.`, 'Copy Failed');
    }

    document.body.removeChild(textArea);
  }

  printCredentials(): void {
    window.print();
  }

  onGoHome(): void {
    this.router.navigate(['/main-applcation-login']);
  }
}
